/**
 * TG Status Calculator
 *
 * Calculates trädgårdsmästare (TG) status based on assignment data and timestamps.
 * This replaces implicit status tracking with explicit status calculation.
 */

import { isToday, isPast, parseISO } from 'date-fns'
import type { TgWorkStatus, AssignmentStatusV2 } from '../types/statusTypes'

// =============================================
// TYPES
// =============================================

export interface AssignmentSubcontractorData {
  // Existing fields
  reported_at: string | null
  is_draft: boolean
  admin_feedback: string | null
  feedback_at: string | null
  attested_at: string | null

  // New V2 fields
  tg_status?: TgWorkStatus
  declined_at?: string | null
  declined_reason?: string | null
}

export interface AssignmentData {
  status: AssignmentStatusV2
  start_date: string | null
  start_time: string | null
}

// =============================================
// TG WORK STATUS CALCULATION
// =============================================

/**
 * Calculate TG work status based on assignment and subcontractor data.
 *
 * Status flow:
 * preliminar_tilldelad -> tilldelad -> ej_rapporterad -> rapporterad -> godkand
 *                                   \                 \-> komplettering -/
 *                                    \-> avsagt
 */
export function calculateTgWorkStatus(
  tg: AssignmentSubcontractorData,
  assignment: AssignmentData
): TgWorkStatus {
  // 1. Check if TG has declined
  if (tg.declined_at) {
    return 'avsagt'
  }

  // 2. Check if report is attested (godkänd)
  if (tg.attested_at) {
    return 'godkand'
  }

  // 3. Check if komplettering is requested (has feedback but no new report)
  if (tg.admin_feedback && tg.feedback_at) {
    // If feedback was given after the last report, status is komplettering
    if (!tg.reported_at || (tg.reported_at && tg.feedback_at > tg.reported_at)) {
      return 'komplettering'
    }
  }

  // 4. Check if report is submitted (not draft)
  if (tg.reported_at && !tg.is_draft) {
    return 'rapporterad'
  }

  // 5. Check if assignment is in execution phase (date has occurred)
  if (isAssignmentInProgress(assignment)) {
    return 'ej_rapporterad'
  }

  // 6. Check if assignment is confirmed (planerad)
  if (assignment.status === 'planerad') {
    return 'tilldelad'
  }

  // 7. Default: preliminär status
  return 'preliminar_tilldelad'
}

/**
 * Check if assignment date has occurred (same day or past)
 */
export function isAssignmentInProgress(assignment: AssignmentData): boolean {
  if (!assignment.start_date) {
    return false
  }

  // Check assignment status first
  if (assignment.status === 'pagaende' ||
      assignment.status === 'granskning' ||
      assignment.status === 'attesterad' ||
      assignment.status === 'slutford') {
    return true
  }

  // Check if date is today or past
  try {
    const assignmentDate = parseISO(assignment.start_date)
    return isToday(assignmentDate) || isPast(assignmentDate)
  } catch {
    return false
  }
}

// =============================================
// STATUS TRANSITION HELPERS
// =============================================

/**
 * Get valid next statuses for TG work status
 */
export function getValidTgStatusTransitions(currentStatus: TgWorkStatus): TgWorkStatus[] {
  const transitions: Record<TgWorkStatus, TgWorkStatus[]> = {
    'preliminar_tilldelad': ['tilldelad', 'avsagt'],
    'tilldelad': ['ej_rapporterad', 'avsagt'],
    'ej_rapporterad': ['rapporterad', 'avsagt'],
    'rapporterad': ['godkand', 'komplettering'],
    'komplettering': ['rapporterad'],
    'godkand': [], // Terminal state
    'avsagt': []   // Terminal state
  }

  return transitions[currentStatus] || []
}

/**
 * Check if TG status can be manually changed to target status
 */
export function canTransitionTgStatus(
  currentStatus: TgWorkStatus,
  targetStatus: TgWorkStatus
): boolean {
  const validTransitions = getValidTgStatusTransitions(currentStatus)
  return validTransitions.includes(targetStatus)
}

// =============================================
// BULK STATUS HELPERS
// =============================================

/**
 * Check if all TGs have completed reporting (rapporterad, godkand, or avsagt)
 * Note: avsagt counts as "complete" since declined TGs don't need to report,
 * BUT at least one TG must have actually reported (not just declined)
 */
export function allTgsReported(tgStatuses: TgWorkStatus[]): boolean {
  if (tgStatuses.length === 0) return false

  const completedStatuses: TgWorkStatus[] = ['rapporterad', 'godkand', 'avsagt']
  const allComplete = tgStatuses.every(status => completedStatuses.includes(status))

  // At least one TG must have reported (not just declined)
  const hasActualReport = tgStatuses.some(status => status === 'rapporterad' || status === 'godkand')

  return allComplete && hasActualReport
}

/**
 * Check if all TGs are godkand (approved) or avsagt (declined)
 * Note: avsagt counts as "approved" since declined TGs don't need approval,
 * BUT at least one TG must have actually been approved (not just declined)
 */
export function allTgsApproved(tgStatuses: TgWorkStatus[]): boolean {
  if (tgStatuses.length === 0) return false

  const allApprovedOrDeclined = tgStatuses.every(status => status === 'godkand' || status === 'avsagt')

  // At least one TG must have been approved (not just declined)
  const hasActualApproval = tgStatuses.some(status => status === 'godkand')

  return allApprovedOrDeclined && hasActualApproval
}

/**
 * Check if any TG has declined
 */
export function anyTgDeclined(tgStatuses: TgWorkStatus[]): boolean {
  return tgStatuses.some(status => status === 'avsagt')
}

/**
 * Get count of TGs by status
 */
export function getTgStatusCounts(tgStatuses: TgWorkStatus[]): Record<TgWorkStatus, number> {
  const counts: Record<TgWorkStatus, number> = {
    'preliminar_tilldelad': 0,
    'tilldelad': 0,
    'ej_rapporterad': 0,
    'rapporterad': 0,
    'komplettering': 0,
    'godkand': 0,
    'avsagt': 0
  }

  for (const status of tgStatuses) {
    counts[status]++
  }

  return counts
}

// =============================================
// STATUS UPDATE HELPERS
// =============================================

/**
 * Get fields to update when TG status changes
 */
export function getTgStatusUpdateFields(
  newStatus: TgWorkStatus,
  reason?: string
): Partial<AssignmentSubcontractorData> {
  const now = new Date().toISOString()

  const baseUpdate = {
    tg_status: newStatus,
    tg_status_updated_at: now
  }

  if (newStatus === 'avsagt' && reason) {
    return {
      ...baseUpdate,
      declined_at: now,
      declined_reason: reason
    }
  }

  return baseUpdate
}

/**
 * Recalculate and get updated TG statuses for all TGs on an assignment
 */
export function recalculateTgStatuses(
  tgs: AssignmentSubcontractorData[],
  assignment: AssignmentData
): TgWorkStatus[] {
  return tgs.map(tg => calculateTgWorkStatus(tg, assignment))
}
